// src/pages/Companies.js
import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { AuthContext } from "../context/AuthContext";

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

const COUNTRIES = [
  { code: "NL", flag: "🇳🇱", label: "Netherlands",    lookup: true,   hint: "Search by company name or KvK number" },
  { code: "GB", flag: "🇬🇧", label: "United Kingdom",  lookup: true,   hint: "Search by company name or registration number" },
  { code: "DE", flag: "🇩🇪", label: "Germany",         lookup: "link", hint: "Look up on Handelsregister, then fill in manually", link: "https://www.handelsregister.de/rp_web/normalesuche/welcome.xhtml" },
  { code: "BE", flag: "🇧🇪", label: "Belgium",         lookup: "link", hint: "Look up on KBO registry, then fill in manually", link: "https://kbopub.economie.fgov.be/kbopub/zoeknaamfonetischform.html?lang=en" },
  { code: "FR", flag: "🇫🇷", label: "France",          lookup: true,   hint: "Search by company name or SIREN" },
  { code: "DK", flag: "🇩🇰", label: "Denmark",         lookup: true,   hint: "Search by company name or CVR number" },
  { code: "NO", flag: "🇳🇴", label: "Norway",          lookup: true,   hint: "Search by company name or org. number" },
  { code: "CZ", flag: "🇨🇿", label: "Czech Republic",  lookup: true,   hint: "Search by company name or IČO" },
  { code: "PL", flag: "🇵🇱", label: "Poland",          lookup: "kbo",  hint: "Enter KRS number (10 digits, e.g. 0000010681)" },
  { code: "ES", flag: "🇪🇸", label: "Spain",           lookup: false,  hint: "Fill in company details manually" },
  { code: "SE", flag: "🇸🇪", label: "Sweden",          lookup: false,  hint: "Fill in company details manually" },
  { code: "FI", flag: "🇫🇮", label: "Finland",         lookup: false,  hint: "Fill in company details manually" },
  { code: "AT", flag: "🇦🇹", label: "Austria",         lookup: false,  hint: "Fill in company details manually" },
  { code: "IE", flag: "🇮🇪", label: "Ireland",         lookup: false,  hint: "Fill in company details manually" },
  { code: "PT", flag: "🇵🇹", label: "Portugal",        lookup: false,  hint: "Fill in company details manually" },
  { code: "IT", flag: "🇮🇹", label: "Italy",           lookup: false,  hint: "Fill in company details manually" },
  { code: "GR", flag: "🇬🇷", label: "Greece",          lookup: false,  hint: "Fill in company details manually" },
  { code: "HR", flag: "🇭🇷", label: "Croatia",         lookup: false,  hint: "Fill in company details manually" },
  { code: "RO", flag: "🇷🇴", label: "Romania",         lookup: false,  hint: "Fill in company details manually" },
  { code: "OTHER", flag: "🌐", label: "Other",         lookup: false,  hint: "Fill in company details manually" },
];

function RegistrySearch({ country, token, onSelect, numberOnly }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [err, setErr] = useState("");
  const timerRef = useRef(null);
  const cfg = COUNTRIES.find(c => c.code === country);

  function handleChange(e) {
    const val = e.target.value;
    setQuery(val);
    setErr("");
    clearTimeout(timerRef.current);
    if (val.length < 3) { setResults([]); return; }
    timerRef.current = setTimeout(() => doSearch(val), numberOnly ? 600 : 400);
  }

  async function doSearch(q) {
    setSearching(true);
    setResults([]);
    try {
      const res = await api(`/companies/lookup?country=${country}&q=${encodeURIComponent(q)}`, { token });
      setResults(Array.isArray(res) ? res : []);
      if (Array.isArray(res) && res.length === 0) setErr("No results found.");
    } catch (e) {
      setErr(e?.message || "Registry lookup failed.");
    } finally {
      setSearching(false);
    }
  }

  function handleSelect(item) {
    setQuery("");
    setResults([]);
    setErr("");
    onSelect(item);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ position: "relative" }}>
        <input
          type="text"
          value={query}
          onChange={handleChange}
          placeholder={cfg?.hint || "Search…"}
          style={{ border: "1px solid #d0dcea", borderRadius: 6, padding: "8px 10px", fontSize: 14, width: "100%", boxSizing: "border-box" }}
        />
        {searching && (
          <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#6b8cae" }}>
            Searching…
          </span>
        )}
      </div>
      {err && <div style={{ fontSize: 12, color: "#c0392b" }}>{err}</div>}
      {results.length > 0 && (
        <div style={{ border: "1px solid #d0dcea", borderRadius: 8, background: "white", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", overflow: "hidden" }}>
          {results.map((r, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleSelect(r)}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "10px 14px", background: "none", border: "none",
                borderTop: i > 0 ? "1px solid #e8eef5" : "none",
                cursor: "pointer", fontSize: 14,
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#f4f7fb"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              <div style={{ fontWeight: 500, color: "#1e3a5f" }}>{r.name}</div>
              <div style={{ fontSize: 12, color: "#6b8cae", marginTop: 2 }}>
                {r.registration_number && <span style={{ marginRight: 8 }}>№ {r.registration_number}</span>}
                {r.address && <span>{r.address}</span>}
                {r.status && r.status !== "active" && (
                  <span style={{ marginLeft: 8, color: "#c0392b" }}>({r.status})</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function OskarSearch({ token, onSelect }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [err, setErr] = useState("");
  const timerRef = useRef(null);

  function handleChange(e) {
    const val = e.target.value;
    setQuery(val);
    setErr("");
    clearTimeout(timerRef.current);
    if (val.length < 2) { setResults([]); return; }
    timerRef.current = setTimeout(() => doSearch(val), 300);
  }

  async function doSearch(q) {
    setSearching(true);
    setResults([]);
    try {
      const res = await api(`/companies?q=${encodeURIComponent(q)}`, { token });
      const list = Array.isArray(res?.companies) ? res.companies : [];
      setResults(list);
      if (list.length === 0) setErr("No companies found in Oskar.");
    } catch (e) {
      setErr(e?.message || "Search failed.");
    } finally {
      setSearching(false);
    }
  }

  function handleSelect(item) {
    setQuery("");
    setResults([]);
    setErr("");
    onSelect(item);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ position: "relative" }}>
        <input
          type="text"
          value={query}
          onChange={handleChange}
          placeholder="Search by company name…"
          style={{ border: "1px solid #d0dcea", borderRadius: 6, padding: "8px 10px", fontSize: 14, width: "100%", boxSizing: "border-box" }}
        />
        {searching && (
          <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#6b8cae" }}>
            Searching…
          </span>
        )}
      </div>
      {err && <div style={{ fontSize: 12, color: "#94afc9" }}>{err}</div>}
      {results.length > 0 && (
        <div style={{ border: "1px solid #d0dcea", borderRadius: 8, background: "white", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", overflow: "hidden" }}>
          {results.map((r, i) => (
            <button
              key={r.id}
              type="button"
              onClick={() => handleSelect(r)}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "10px 14px", background: "none", border: "none",
                borderTop: i > 0 ? "1px solid #e8eef5" : "none",
                cursor: "pointer", fontSize: 14,
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#f4f7fb"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              <div style={{ fontWeight: 500, color: "#1e3a5f" }}>{r.name}</div>
              <div style={{ fontSize: 12, color: "#6b8cae", marginTop: 2 }}>
                {[r.country, r.email].filter(Boolean).join(" · ")}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CompanyPreviewCard({ company, onAdd, onDismiss, saving, saved }) {
  return (
    <div style={{
      border: "1px solid #d0dcea", borderRadius: 10, background: "#f4f7fb",
      padding: "16px 18px", display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 15, color: "#1e3a5f" }}>{company.name}</div>
          <div style={{ fontSize: 13, color: "#6b8cae", marginTop: 3 }}>
            {[company.country, company.registration_number && `№ ${company.registration_number}`, company.address].filter(Boolean).join(" · ")}
          </div>
          {company.email && <div style={{ fontSize: 13, color: "#6b8cae", marginTop: 2 }}>{company.email}</div>}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#94afc9", fontSize: 18, lineHeight: 1, padding: "0 0 0 8px" }}
        >
          ×
        </button>
      </div>
      {saved ? (
        <div style={{ fontSize: 13, color: "#2e7d32", fontWeight: 500 }}>✓ Added to company list</div>
      ) : (
        <button
          type="button"
          onClick={onAdd}
          disabled={saving}
          style={{
            alignSelf: "flex-start", padding: "7px 16px",
            background: "#1e3a5f", color: "white", border: "none",
            borderRadius: 7, fontSize: 13, cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? "Adding…" : "Add to company list"}
        </button>
      )}
    </div>
  );
}

function ManualEntryForm({ selectedCountryCode, selectedCountry, token, onSaved, isMobile, prefill }) {
  const [formData, setFormData] = useState({
    name: "", email: "", country: selectedCountry?.code !== "OTHER" ? (selectedCountry?.label || "") : "",
    phone: "", street: "", postcode: "", city: "", website: "", chamber_of_commerce: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      country: selectedCountry && selectedCountryCode !== "OTHER" ? selectedCountry.label : "",
    }));
  }, [selectedCountryCode, selectedCountry]);

  useEffect(() => {
    if (!prefill) return;
    setFormData(prev => ({
      ...prev,
      name: prefill.name || prev.name,
      ...(() => {
        if (!prefill.address) return {};
        const parts = prefill.address.split(",").map(p => p.trim());
        return { street: parts[0] || "", postcode: parts[1] || "", city: parts[2] || "" };
      })(),
      chamber_of_commerce: prefill.registration_number || prev.chamber_of_commerce,
      website: prefill.website || prev.website,
      email: prefill.email || prev.email,
    }));
  }, [prefill]);

  const inputStyle = { border: "1px solid #d0dcea", borderRadius: 6, padding: "8px 10px", fontSize: 14, width: "100%", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontSize: 13, fontWeight: 500, color: "#3a5068", marginBottom: 4 };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!formData.name.trim()) return setErr("Company name is required.");
    if (!formData.country.trim()) return setErr("Country is required.");
    setSaving(true);
    try {
      const res = await api("/companies", {
        method: "POST",
        body: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          country: formData.country.trim(),
          phone: formData.phone.trim() || null,
          address: [formData.street, formData.postcode, formData.city].filter(Boolean).join(", ") || null,
          website: formData.website.trim() || null,
          chamber_of_commerce: formData.chamber_of_commerce.trim() || null,
        },
        token,
      });
      onSaved(res.company || { name: formData.name });
    } catch (e) {
      setErr(e.message || "Error saving company");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {err && <div style={{ fontSize: 13, color: "#c0392b" }}>{err}</div>}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
        <div style={{ gridColumn: isMobile ? undefined : "1 / -1" }}>
          <label style={labelStyle}>Company Name <span style={{ color: "#c0392b" }}>*</span></label>
          <input type="text" name="name" value={formData.name} onChange={handleChange} style={inputStyle} required />
        </div>
        <div>
          <label style={labelStyle}>Country <span style={{ color: "#c0392b" }}>*</span></label>
          <input type="text" name="country" value={formData.country} onChange={handleChange} style={inputStyle} required />
        </div>
        <div>
          <label style={labelStyle}>Chamber of Commerce / Registration No.</label>
          <input type="text" name="chamber_of_commerce" value={formData.chamber_of_commerce} onChange={handleChange} style={inputStyle} />
        </div>
        <div style={{ gridColumn: isMobile ? undefined : "1 / -1" }}>
          <label style={labelStyle}>Street address</label>
          <input type="text" name="street" value={formData.street} onChange={handleChange} style={inputStyle} placeholder="e.g. Kraftværksvej 53" />
        </div>
        <div>
          <label style={labelStyle}>Postal code</label>
          <input type="text" name="postcode" value={formData.postcode} onChange={handleChange} style={inputStyle} placeholder="e.g. 7000" />
        </div>
        <div>
          <label style={labelStyle}>City</label>
          <input type="text" name="city" value={formData.city} onChange={handleChange} style={inputStyle} placeholder="e.g. Fredericia" />
        </div>
      </div>
      <div style={{ borderTop: "1px solid #d0dcea", paddingTop: 14 }}>
        <p style={{ fontSize: 12, color: "#6b8cae", margin: "0 0 14px" }}>Contact details (optional)</p>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
          <div>
            <label style={labelStyle}>Contact Email</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Phone</label>
            <input type="text" name="phone" value={formData.phone} onChange={handleChange} style={inputStyle} />
          </div>
          <div style={{ gridColumn: isMobile ? undefined : "1 / -1" }}>
            <label style={labelStyle}>Website</label>
            <input type="text" name="website" value={formData.website} onChange={handleChange} placeholder="https://" style={inputStyle} />
          </div>
        </div>
      </div>
      <div>
        <button
          type="submit" disabled={saving}
          style={{
            padding: "9px 20px", background: "#1e3a5f", color: "white",
            border: "none", borderRadius: 8, fontSize: 14,
            cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? "Adding…" : "Add company"}
        </button>
      </div>
    </form>
  );
}

function splitAddress(address) {
  if (!address) return { street: "", postcode: "", city: "" };
  if (address.includes(" | ")) {
    const parts = address.split(" | ");
    return { street: parts[0] || "", postcode: parts[1] || "", city: parts[2] || "" };
  }
  const parts = address.split(",").map(p => p.trim());
  return { street: parts[0] || "", postcode: parts[1] || "", city: parts[2] || "" };
}

function CompanyCard({ company: c, role, token, onEdit, navigate }) {
  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState(null);
  const [linksLoading, setLinksLoading] = useState(false);
  const addr = splitAddress(c.address);

  async function handleExpand() {
    const next = !open;
    setOpen(next);
    if (next && links === null) {
      setLinksLoading(true);
      try {
        const res = await api(`/companies/${c.id}/links`, { token });
        setLinks(res);
      } catch {
        setLinks({ vessels: [], inspections: [] });
      } finally {
        setLinksLoading(false);
      }
    }
  }

  const directInspections = links?.inspections || [];
  const vessels = links?.vessels || [];

  return (
    <div style={{
      background: "white", border: "1px solid #d0dcea", borderRadius: 12,
      overflow: "hidden",
    }}>
      {/* Header — always visible */}
      <div
        onClick={handleExpand}
        style={{
          padding: "12px 16px", display: "flex", alignItems: "center",
          justifyContent: "space-between", cursor: "pointer", userSelect: "none",
        }}
      >
        <div style={{ fontWeight: 500, fontSize: 14, color: "#1e3a5f" }}>{c.name}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
            style={{ width: 16, height: 16, transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0deg)", color: "#94afc9", flexShrink: 0 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </div>
      </div>

      {/* Expanded details */}
      {open && (
        <div style={{
          borderTop: "1px solid #e8eef5", padding: "12px 16px 16px",
          display: "flex", flexDirection: "column", gap: 12,
        }}>

          {/* Company details */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 0 }}>
              {(addr.street || addr.postcode || addr.city) && (
                <div style={{ fontSize: 13, color: "#6b8cae", wordBreak: "break-word" }}>
                  {[addr.street, addr.postcode, addr.city].filter(Boolean).join(", ")}
                </div>
              )}
              {c.country && <div style={{ fontSize: 13, color: "#6b8cae" }}>{c.country}</div>}
              {c.chamber_of_commerce && <div style={{ fontSize: 13, color: "#6b8cae" }}>№ {c.chamber_of_commerce}</div>}
              {c.email && <div style={{ fontSize: 13, color: "#6b8cae", wordBreak: "break-all" }}>{c.email}</div>}
              {c.phone && <div style={{ fontSize: 13, color: "#6b8cae" }}>{c.phone}</div>}
              {c.website && (
                <a href={c.website} target="_blank" rel="noreferrer"
                  style={{ fontSize: 13, color: "#63b3ed", textDecoration: "none", wordBreak: "break-all" }}>
                  {c.website}
                </a>
              )}
            </div>
            {["inspector", "admin"].includes(role) && (
              <button
                type="button"
                onClick={onEdit}
                style={{ padding: "4px 12px", border: "1px solid #d0dcea", borderRadius: 6, background: "white", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap", color: "#1e3a5f", flexShrink: 0 }}
              >
                Edit
              </button>
            )}
          </div>

          {linksLoading && (
            <div style={{ fontSize: 13, color: "#94afc9" }}>Loading links…</div>
          )}

          {!linksLoading && links && (
            <>
              {/* Vessels */}
              {vessels.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#94afc9", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Vessels
                  </div>
                  {vessels.map(v => (
                    <div key={v.id} style={{
                      background: "#f4f7fb", borderRadius: 8, padding: "8px 12px",
                      display: "flex", flexDirection: "column", gap: 4,
                    }}>
                      <button
                        type="button"
                        onClick={() => navigate(`/vessels`)}
                        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", fontSize: 13, fontWeight: 500, color: "#1e3a5f" }}
                      >
                        {v.name} {v.mmsi && <span style={{ fontWeight: 400, color: "#94afc9" }}>· {v.mmsi}</span>}
                      </button>
                      {v.last_inspection_id ? (
                        <div style={{ fontSize: 12, color: "#6b8cae" }}>
                          Last inspection:{" "}
                          {v.last_inspection_accessible ? (
                            <button
                              type="button"
                              onClick={() => navigate(`/inspections/${v.last_inspection_id}`)}
                              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 12, color: "#63b3ed" }}
                            >
                              {v.last_inspection_name || `Inspection #${v.last_inspection_id}`}
                              {v.last_inspection_date && ` — ${v.last_inspection_date}`}
                            </button>
                          ) : (
                            <span>{v.last_inspection_name || `Inspection #${v.last_inspection_id}`}{v.last_inspection_date && ` — ${v.last_inspection_date}`}</span>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: "#94afc9" }}>No inspections on record</div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Direct inspections */}
              {directInspections.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#94afc9", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Inspections
                  </div>
                  {directInspections.map(i => (
                    <div key={i.id} style={{
                      background: "#f4f7fb", borderRadius: 8, padding: "8px 12px",
                      display: "flex", flexDirection: "column", gap: 4,
                    }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                        {i.accessible ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/inspections/${i.id}`)}
                            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", fontSize: 13, fontWeight: 500, color: "#63b3ed", flex: 1, minWidth: 0, overflowWrap: "break-word", whiteSpace: "normal", display: "block", width: "100%" }}
                          >
                            {i.name}
                          </button>
                        ) : (
                          <div style={{ fontSize: 13, fontWeight: 500, color: "#1e3a5f", flex: 1, minWidth: 0, overflowWrap: "break-word" }}>{i.name}</div>
                        )}
                        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                          <span style={{
                            fontSize: 11, padding: "2px 8px", borderRadius: 10,
                            background: i.role === "inspector" ? "#e8eef5" : "#fef9ec",
                            color: i.role === "inspector" ? "#3a5068" : "#92610a",
                            fontWeight: 500,
                          }}>
                            {i.role === "inspector" ? "Inspector" : "Subject"}
                          </span>
                          <span style={{
                            fontSize: 11, padding: "2px 8px", borderRadius: 10,
                            background: "#e8eef5", color: "#6b8cae", fontWeight: 500,
                          }}>
                            {i.status}
                          </span>
                        </div>
                      </div>
                      {i.date && <div style={{ fontSize: 12, color: "#94afc9" }}>{i.date}</div>}
                    </div>
                  ))}
                </div>
              )}

              {vessels.length === 0 && directInspections.length === 0 && (
                <div style={{ fontSize: 13, color: "#94afc9" }}>No vessels or inspections linked to this company.</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function Companies() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();
  const { token: ctxToken } = useContext(AuthContext);
  const activeToken = token || ctxToken;
  const role = user?.role;
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 640;

  // Company list
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listErr, setListErr] = useState("");
  const [listOpen, setListOpen] = useState(true);
  const [successMessage, setSuccessMessage] = useState(location.state?.successMessage || "");
  const returnTo = new URLSearchParams(location.search).get("returnTo");

  // Find & add accordion
  const [addOpen, setAddOpen] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const [previewCompany, setPreviewCompany] = useState(null);
  const [previewSaving, setPreviewSaving] = useState(false);
  const [previewSaved, setPreviewSaved] = useState(false);
  const [registryPrefill, setRegistryPrefill] = useState(null);
  const [searchMode, setSearchMode] = useState("registry"); // "registry" | "oskar"

  const selectedCountry = COUNTRIES.find(c => c.code === selectedCountryCode);

  async function loadCompanies() {
    setLoading(true);
    setListErr("");
    try {
      const res = await api("/companies", { token: activeToken });
      setItems(Array.isArray(res?.companies) ? res.companies : []);
    } catch (e) {
      setListErr(e?.message || "Failed to load companies");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!activeToken) return;
    loadCompanies();
  }, [activeToken]);

  function handleCountrySelect(code) {
    setSelectedCountryCode(code);
    setPreviewCompany(null);
    setPreviewSaved(false);
    setRegistryPrefill(null);
  }

  function handleRegistrySelect(item) {
    setRegistryPrefill({
      name: item.name || "",
      address: item.address || "",
      registration_number: item.registration_number || "",
      website: item.website || "",
      email: item.email || "",
    });
    setPreviewCompany(null);
    setPreviewSaved(false);
  }

  function handleOskarSelect(item) {
    // Already in Oskar — show as preview but mark as already saved
    setPreviewCompany({
      ...item,
      registration_number: item.chamber_of_commerce || "",
      _alreadyInOskar: true,
    });
    setPreviewSaved(false);
  }

  async function handleAddFromPreview() {
    if (!previewCompany) return;
    if (previewCompany._alreadyInOskar) {
      if (returnTo) {
        navigate(returnTo, { state: { newCompanyId: previewCompany.id, newCompanyName: previewCompany.name } });
        return;
      }
      setPreviewSaved(true);
      await loadCompanies();
      return;
    }
    setPreviewSaving(true);
    try {
      await api("/companies", {
        method: "POST",
        body: {
          name: previewCompany.name,
          email: previewCompany.email || null,
          country: previewCompany.country || null,
          phone: null,
          address: previewCompany.address || null,
          website: previewCompany.website || null,
          chamber_of_commerce: previewCompany.chamber_of_commerce || null,
        },
        token: activeToken,
      });
      setPreviewSaved(true);
      setSuccessMessage(`${previewCompany.name} has been added.`);
      await loadCompanies();
    } catch (e) {
      // If 409 (already exists), still show as saved
      if (e?.message?.includes("409") || e?.message?.toLowerCase().includes("already")) {
        setPreviewSaved(true);
        await loadCompanies();
      }
    } finally {
      setPreviewSaving(false);
    }
  }

  function handleManualSaved(company) {
    if (returnTo) {
      navigate(returnTo, { state: { newCompanyId: company?.id, newCompanyName: company?.name } });
      return;
    }
    setPreviewCompany({ ...company, _justSaved: true });
    setPreviewSaved(true);
    setRegistryPrefill(null);
    setSuccessMessage(`${company?.name || "Company"} has been added.`);
    loadCompanies();
  }

  // Chevron icon
  function Chevron({ open }) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
        style={{ width: 16, height: 16, transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
      </svg>
    );
  }

  const sectionHeaderStyle = {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "14px 18px", cursor: "pointer", userSelect: "none",
    borderRadius: 12,
  };

  const sectionCardStyle = {
    background: "white", borderRadius: 12, border: "1px solid #d0dcea",
    marginBottom: 16, overflow: "hidden",
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", paddingTop: 32 }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, color: "#1e3a5f", margin: "0 0 20px" }}>Companies</h1>

      {successMessage && (
        <div style={{
          fontSize: 14, color: "#2e7d32", border: "1px solid #a8d5b5",
          borderRadius: 8, padding: "10px 16px", marginBottom: 16, background: "#e6f4ea",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span>✓ {successMessage}</span>
          <button type="button" onClick={() => setSuccessMessage("")}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#2e7d32", fontSize: 16, lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* ── Find & add a company ── */}
      <div style={sectionCardStyle}>
        <div
          style={{ ...sectionHeaderStyle, background: addOpen ? "#f4f7fb" : "white" }}
          onClick={() => setAddOpen(o => !o)}
          role="button"
          aria-expanded={addOpen}
        >
          <span style={{ fontWeight: 600, fontSize: 15, color: "#1e3a5f" }}>Find and add a company</span>
          <Chevron open={addOpen} />
        </div>

        {addOpen && (
          <div style={{ padding: "0 18px 20px", borderTop: "1px solid #e8eef5" }}>

            {/* Search mode toggle */}
            <div style={{ display: "flex", gap: 8, marginTop: 16, marginBottom: 16 }}>
              <button
                type="button"
                onClick={() => { setSearchMode("registry"); setPreviewCompany(null); setPreviewSaved(false); }}
                style={{
                  padding: "6px 14px", borderRadius: 6, fontSize: 13,
                  border: searchMode === "registry" ? "1.5px solid #1e3a5f" : "1px solid #d0dcea",
                  background: searchMode === "registry" ? "#1e3a5f" : "white",
                  color: searchMode === "registry" ? "white" : "#6b8cae",
                  cursor: "pointer", fontWeight: searchMode === "registry" ? 500 : 400,
                }}
              >
                Official registry
              </button>
              <button
                type="button"
                onClick={() => { setSearchMode("oskar"); setPreviewCompany(null); setPreviewSaved(false); setSelectedCountryCode(""); }}
                style={{
                  padding: "6px 14px", borderRadius: 6, fontSize: 13,
                  border: searchMode === "oskar" ? "1.5px solid #1e3a5f" : "1px solid #d0dcea",
                  background: searchMode === "oskar" ? "#1e3a5f" : "white",
                  color: searchMode === "oskar" ? "white" : "#6b8cae",
                  cursor: "pointer", fontWeight: searchMode === "oskar" ? 500 : 400,
                }}
              >
                Search Oskar
              </button>
            </div>

            {/* Official registry mode */}
            {searchMode === "registry" && (
              <>
                {/* Country picker */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "#3a5068", marginBottom: 8 }}>Select country</div>
                  <div style={{ display: "grid", gridTemplateColumns: `repeat(${isMobile ? 5 : 10}, 1fr)`, gap: 6 }}>
                    {COUNTRIES.map(c => (
                      <button
                        key={c.code}
                        type="button"
                        onClick={() => handleCountrySelect(c.code)}
                        title={c.label}
                        style={{
                          display: "flex", flexDirection: "column", alignItems: "center",
                          padding: "6px 2px", borderRadius: 8, fontSize: 20,
                          border: selectedCountryCode === c.code ? "2px solid #1e3a5f" : "1px solid #d0dcea",
                          background: selectedCountryCode === c.code ? "#e8eef5" : "white",
                          cursor: "pointer", gap: 2,
                        }}
                      >
                        <span>{c.flag}</span>
                        <span style={{ fontSize: 8, color: "#6b8cae", lineHeight: 1.2, textAlign: "center" }}>
                          {c.code === "OTHER" ? "Other" : c.code}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Registry search or link or manual */}
                {selectedCountry && (
                  <div style={{ marginBottom: 16 }}>

                    {/* Name search */}
                    {selectedCountry.lookup === true && (
                      <div style={{ padding: 14, background: "#f4f7fb", borderRadius: 8, border: "1px solid #d0dcea", marginBottom: previewCompany ? 14 : 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: "#1e3a5f", marginBottom: 8 }}>
                          {selectedCountry.flag} Look up on official registry
                        </div>
                        <RegistrySearch country={selectedCountryCode} token={activeToken} onSelect={handleRegistrySelect} />
                      </div>
                    )}

                    {/* KRS number lookup */}
                    {selectedCountry.lookup === "kbo" && (
                      <div style={{ padding: 14, background: "#f4f7fb", borderRadius: 8, border: "1px solid #d0dcea", marginBottom: previewCompany ? 14 : 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: "#1e3a5f", marginBottom: 8 }}>
                          {selectedCountry.flag} Look up by registration number
                        </div>
                        <RegistrySearch country={selectedCountryCode} token={activeToken} onSelect={handleRegistrySelect} numberOnly />
                      </div>
                    )}

                    {/* Official portal link */}
                    {selectedCountry.lookup === "link" && (
                      <div style={{ padding: 14, background: "#f4f7fb", borderRadius: 8, border: "1px solid #d0dcea", marginBottom: 14 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: "#1e3a5f", marginBottom: 6 }}>
                          {selectedCountry.flag} Look up on official register
                        </div>
                        <p style={{ fontSize: 13, color: "#6b8cae", margin: "0 0 10px" }}>
                          Search the official registry, then fill in the details below.
                        </p>
                        <a
                          href={selectedCountry.link || "#"}
                          target="_blank"
                          rel="noreferrer"
                          style={{ display: "inline-block", padding: "7px 14px", background: "#1e3a5f", color: "white", borderRadius: 6, fontSize: 13, textDecoration: "none" }}
                        >
                          Open official registry →
                        </a>
                      </div>
                    )}

                    {/* Saved confirmation card */}
                    {previewCompany && previewCompany._justSaved && (
                      <CompanyPreviewCard
                        company={previewCompany}
                        onAdd={() => {}}
                        onDismiss={() => { setPreviewCompany(null); setPreviewSaved(false); }}
                        saving={false}
                        saved={true}
                      />
                    )}

                    {/* Manual entry form — shown when country selected */}
                    {!previewSaved && (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ fontSize: 13, color: "#6b8cae", marginBottom: 12 }}>
                          {selectedCountry.lookup === true && selectedCountryCode !== "NL"
                            ? "Or fill in manually:"
                            : "Fill in company details:"}
                        </div>
                        <ManualEntryForm
                          selectedCountryCode={selectedCountryCode}
                          selectedCountry={selectedCountry}
                          token={activeToken}
                          onSaved={handleManualSaved}
                          isMobile={isMobile}
                          prefill={registryPrefill}
                        />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Oskar search mode */}
            {searchMode === "oskar" && (
              <div>
                <div style={{ fontSize: 13, color: "#6b8cae", marginBottom: 10 }}>
                  Search for a company already in the Oskar database.
                </div>
                <OskarSearch token={activeToken} onSelect={handleOskarSelect} />
                {previewCompany && previewCompany._alreadyInOskar && (
                  <div style={{ marginTop: 12 }}>
                    <CompanyPreviewCard
                      company={previewCompany}
                      onAdd={handleAddFromPreview}
                      onDismiss={() => { setPreviewCompany(null); setPreviewSaved(false); }}
                      saving={previewSaving}
                      saved={previewSaved}
                    />
                  </div>
                )}
              </div>
            )}

          </div>
        )}
      </div>

      {/* ── Company list ── */}
      <div style={{ ...sectionCardStyle, background: "#e2eaf4", border: "none" }}>
        <div
          style={{ ...sectionHeaderStyle, background: listOpen ? "#f4f7fb" : "white" }}
          onClick={() => setListOpen(o => !o)}
          role="button"
          aria-expanded={listOpen}
        >
          <span style={{ fontWeight: 600, fontSize: 15, color: "#1e3a5f" }}>
            Company list {items.length > 0 && <span style={{ fontWeight: 400, color: "#94afc9", fontSize: 13 }}>({items.length})</span>}
          </span>
          <Chevron open={listOpen} />
        </div>

        {listOpen && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 12 }}>
            {loading && <div style={{ padding: "16px 18px", fontSize: 14, color: "#6b8cae" }}>Loading…</div>}
            {!loading && listErr && (
              <div style={{ padding: "16px 18px", fontSize: 14, color: "#c0392b" }}>{listErr}</div>
            )}
            {!loading && !listErr && items.length === 0 && (
              <div style={{ padding: "16px 18px", fontSize: 14, color: "#6b8cae" }}>No companies added yet.</div>
            )}
            {!loading && !listErr && items.map((c) => (
              <CompanyCard
                key={c.id}
                company={c}
                role={role}
                token={activeToken}
                navigate={navigate}
                onEdit={() => navigate(`/companies/${c.id}/edit`)}
              />
            ))}
          </div>
        )}
      </div>

    </div>
  );
}